import React, { ReactNode } from 'react';
import { MeterEntriesConfirmationMessage } from '../../../../app/content/meter/components/metersForm/components/confirmationMessage';
import { MS_IN_SECOND, SECONDS_IN_MINUTE } from '../../../../common/constants/date';
import { buildModalNotificationFromMeterEntriesValidationError } from '../../../../app/content/meter/components/metersForm/utils';
import { HttpWrapperOptions, httpService } from '../../../../app/shared/httpWrapper';
import { AccountDetailsViewModel } from '../../../../../../common/model/account/accountDetailsViewModel';
import { appUrls } from '../../../../common/appUrls';
import { setAccounts, setAccountsLoading, setAccountsLoadingError, setMeterEntriesSending } from '../../../reducers/accounts/meterAndEntries';
import { accountsSelector, accountsSliceSelector, accountsTricMetersSliceSelector, accountsWithoutMetersSliceSelector, clientCurrentRegionSelector } from '../../../selectors';
import { AppThunk } from '../../../reducers';
import { modalNotificationsPush } from '../..';
import { MeterEntriesValidateSuccess } from '../../../../common/model/meterReadings/meterEntriesValidateSuccess';
import { getApiRoute } from '../../../../app/shared/pipes';
import {
    setAccountsWithoutMeters,
    setAccountsWithoutMetersLoading,
    setAccountsWithoutMetersLoadingError
} from '../../../reducers/accounts/withoutMeterAndEntires';
import { MeterEntriesCreator } from '../../../../../../common/model/meterReadings/meterEntriesCreator';
import { getEnvBool } from 'app/shared/utils/get-env';
import { featureFlagBoolean } from 'app/shared/utils/lkFeatureFlags';
import { getDeviationConsuptionStatus } from 'redux/utils/deviationConsuptions';
import { setDidClientHasTricAccount, setTricMeterEntriesSending, setTricMeters, setTricMetersLoading, setTricMetersLoadingError, updateMeterReadingsByMeterId } from 'redux/reducers/accounts/tricMeters';
import { CreateMeterEntriesRequest, CreateMeterEntriesResponse, MeterTricResponse, TricServiceCode, TricServiceNameByCode } from 'common/model/meterReadings/tricModels';
import { getTricServiceIcon } from 'app/shared/utils/tric';

export const getAccountsWithMetersAndEntries = (): AppThunk => async (dispatch, getState) => {
    const { accountsLoading } = accountsSliceSelector(getState());
    const currentRegion = clientCurrentRegionSelector(getState());

    if (accountsLoading) {
        return;
    }

    dispatch(setAccountsLoadingError(null));
    dispatch(setAccountsLoading(true));

    try {
        const endpointURL = getApiRoute(appUrls.api_get_client_accounts_with_meter_and_entries, {}, { region: currentRegion || undefined });
        const result = await httpService.get<AccountDetailsViewModel[]>(endpointURL);
        dispatch(setAccounts(result));
    } catch (err) {
        // Change items state to an empty array for possible future reset to null, which triggers items loading
        setAccounts([]);
        // eslint-disable-next-line no-console
        console.error(err);
        const errorMessage = 'Произошла ошибка при загрузке списка счетов';
        dispatch(setAccountsLoadingError(errorMessage));

        dispatch(modalNotificationsPush({
            title: 'Ошибка',
            message: 'Произошла ошибка при загрузке списка счетов. Попробуйте позже',
            type: 'error',
        }));
    }
    dispatch(setAccountsLoading(false));
};

export const getAccountsWithoutMetersAndEntries = (): AppThunk => async (dispatch, getState) => {
    const { accountsLoading } = accountsWithoutMetersSliceSelector(getState());
    const currentRegion = clientCurrentRegionSelector(getState());

    if (accountsLoading) {
        return;
    }

    dispatch(setAccountsWithoutMetersLoadingError(null));
    dispatch(setAccountsWithoutMetersLoading(true));

    try {
        const endpointURL = getApiRoute(appUrls.api_get_client_accounts_without_meter_and_entries,
            {},
            { region: currentRegion || undefined }
        );
        const result = await httpService.get<AccountDetailsViewModel[]>(endpointURL);
        dispatch(setAccountsWithoutMeters(result));
    } catch (err) {
        // Change items state to an empty array for possible future reset to null, which triggers items loading
        setAccountsWithoutMeters([]);
        // eslint-disable-next-line no-console
        console.error(err);
        const errorMessage = 'Произошла ошибка при загрузке списка счетов';
        dispatch(setAccountsWithoutMetersLoadingError(errorMessage));

        dispatch(modalNotificationsPush({
            title: 'Ошибка',
            message: 'Произошла ошибка при загрузке списка счетов. Попробуйте позже',
            type: 'error',
        }));
    }
    dispatch(setAccountsLoading(false));
};

export const sendMeterEntries = (
    meterEntries: MeterEntriesCreator[],
    accountId?: string,
    onSuccess?: () => Promise<void> | void,
    onPaynow?: (entries: MeterEntriesValidateSuccess[]) => Promise<void> | void,
    isDemoMode?: boolean,
): AppThunk => async (dispatch, getState) => {
    const { meterEntriesSending, accounts } = accountsSliceSelector(getState());

    const currentMeters = accounts?.find(el => el.id === accountId);


    if (meterEntriesSending) {
        return;
    }

    dispatch(setMeterEntriesSending(true));

    try {
        const httpWrapperOptions = new HttpWrapperOptions();
        httpWrapperOptions.queryParams = { isValidate: true, accountId };
        httpWrapperOptions.Timeout(MS_IN_SECOND * SECONDS_IN_MINUTE);

        // FIXME неправильно указан тип entries - не массив!
        const entries = await httpService.post<{ [key: string]: MeterEntriesValidateSuccess }>(
            appUrls.api_post_meter_entries,
            meterEntries,
            httpWrapperOptions
        );

        const lastEntries: (Partial<MeterEntriesCreator & { serviceName: string, date: Date, serviceId: string }>)[] = [];

        const currentMeterEntriesIds = meterEntries.map(el => el.meterScaleId);

        currentMeters?.services.forEach(service => {
            service.meters.forEach(meter => {
                meter.scales.forEach(scale => {
                    if (currentMeterEntriesIds.includes(scale.id ?? '')) {
                        lastEntries.push({
                            value: scale.lastEntry?.value,
                            meterScaleId: scale.id,
                            serviceName: service.service.name,
                            date: new Date(scale.lastEntry?.date ?? '' as string),
                            serviceId: service.serviceId
                        });
                    }
                });
            });
        });

        Object.keys(entries).forEach((entryName: string) => {

            if (!lastEntries.length) {
                entries[entryName].status = 'normal';
                return;
            }

            lastEntries.forEach(lastEntry => {
                if (entries[entryName].serviceId === lastEntry.serviceId) {
                    if (lastEntry.date && entries[entryName].averageDailyConsumption) {
                        entries[entryName].status = getDeviationConsuptionStatus(
                            entries[entryName].averageDailyConsumption as number, entries[entryName].consumption, lastEntry.date);
                    }
                    else {
                        entries[entryName].status = 'normal';
                    }
                }
            });
        });

        dispatch(modalNotificationsPush({
            title: 'Подтвердите ввод показаний',
            message: React.createElement(MeterEntriesConfirmationMessage, { entries, isValidate: true }),
            cancelButtonTitle: 'Отменить',
            onCancel: () => {
                dispatch(setMeterEntriesSending(false));
            },
            okButtonTitle: 'Подтвердить',
            onOk: async () => {
                try {
                    const options = new HttpWrapperOptions();
                    options.queryParams = { accountId };
                    options.Timeout(MS_IN_SECOND * SECONDS_IN_MINUTE);

                    const savedEntries = isDemoMode ? entries : await httpService.post<{ [key: string]: MeterEntriesValidateSuccess }>(
                        appUrls.api_post_meter_entries,
                        meterEntries,
                        options
                    );

                    dispatch(modalNotificationsPush({
                        title: 'Ваши показания приняты',
                        message: React.createElement(MeterEntriesConfirmationMessage, { entries: savedEntries, isValidate: false }),
                        type: 'success',
                        okButtonTitle: featureFlagBoolean('paynow') ? 'Оплатить сейчас' : undefined,
                        onOk: () => {
                            if (!onPaynow) {
                                return;
                            }

                            // ToDo: remove when new data come in
                            // now MeterEntriesValidateSuccess is Record<[service name], MeterEntriesValidateSuccess>
                            if (!Array.isArray(savedEntries)) {
                                onPaynow(Object.values(savedEntries));
                                return;
                            }

                            onPaynow(savedEntries);
                        },
                        cancelButtonTitle: 'Закрыть',
                        onCancel: () => {
                            dispatch(setMeterEntriesSending(false));
                        }
                    }));
                    onSuccess?.();

                } catch (err: any) {
                    dispatch(modalNotificationsPush({
                        title: 'Показания не приняты',
                        message: err?.data?.errorDetails ?? 'Попробуйте позже',
                        type: 'error',
                    }));
                } finally {
                    dispatch(setMeterEntriesSending(false));
                }
            },
            type: 'warning',
        }));
    } catch (err: any) {
        const modalNotification = buildModalNotificationFromMeterEntriesValidationError(err);
        dispatch(modalNotificationsPush(modalNotification));

        dispatch(setMeterEntriesSending(false));
    }
};

export const updateMeterEntries = (
    meterEntries: MeterEntriesCreator[],
    accountId: string,
    onSuccess?: () => Promise<void> | void,
    isDemoMode?: boolean,
): AppThunk => async (dispatch, getState) => {
    const { meterEntriesSending } = accountsSliceSelector(getState());

    if (meterEntriesSending) {
        return;
    }

    dispatch(setMeterEntriesSending(true));

    try {
        const httpWrapperOptions = new HttpWrapperOptions();
        httpWrapperOptions.queryParams = { isValidate: true, accountId };
        httpWrapperOptions.Timeout(MS_IN_SECOND * SECONDS_IN_MINUTE);

        const entries = await httpService.put<{ [key: string]: MeterEntriesValidateSuccess }>(
            appUrls.api_put_meter_entries,
            meterEntries,
            httpWrapperOptions
        );

        dispatch(modalNotificationsPush({
            title: 'Подтвердите ввод показаний',
            message: React.createElement(MeterEntriesConfirmationMessage, { entries, isValidate: true }),
            cancelButtonTitle: 'Отменить',
            onCancel: () => {
                dispatch(setMeterEntriesSending(false));
            },
            okButtonTitle: 'Подтвердить',
            onOk: async () => {
                try {
                    const options = new HttpWrapperOptions();
                    options.queryParams = { accountId };
                    options.Timeout(MS_IN_SECOND * SECONDS_IN_MINUTE);

                    const savedEntries = isDemoMode ? entries : await httpService.put<{ [key: string]: MeterEntriesValidateSuccess }>(
                        appUrls.api_put_meter_entries,
                        meterEntries,
                        options
                    );

                    dispatch(modalNotificationsPush({
                        title: 'Ваши показания приняты',
                        message: React.createElement(MeterEntriesConfirmationMessage, { entries: savedEntries, isValidate: false }),
                        type: 'success',
                    }));
                    onSuccess?.();

                } catch (err: any) {
                    dispatch(modalNotificationsPush({
                        title: 'Показания не приняты',
                        message: err?.data?.errorDetails ?? 'Попробуйте позже',
                        type: 'error',
                    }));
                } finally {
                    dispatch(setMeterEntriesSending(false));
                }
            },
            type: 'warning',
        }));
    } catch (err: any) {
        const modalNotification = buildModalNotificationFromMeterEntriesValidationError(err);
        dispatch(modalNotificationsPush(modalNotification));

        dispatch(setMeterEntriesSending(false));
    }
};

export const getTricMeters = (accountId: string, tricAccountId: string): AppThunk => async (dispatch, getState) => {
    const { accountsLoading } = accountsSliceSelector(getState());

    if (accountsLoading) {
        return;
    }

    dispatch(setTricMetersLoading(true));

    try {
        const endpointURL = getApiRoute(appUrls.api_get_client_tric_meters, { accountId: tricAccountId });
        const result = await httpService.get<MeterTricResponse>(endpointURL);
        dispatch(setDidClientHasTricAccount(true));
        dispatch(setTricMeters({ [accountId]: result }));
    } catch (err) {
        console.error(err);
        const errorMessage = 'Произошла ошибка при загрузке списка ПУ ТРИЦ';
        dispatch(setTricMetersLoadingError(errorMessage));

        // по запросу убрано уведомление об ошибке получения ПУ ТРИЦ
        // dispatch(modalNotificationsPush({
        //     title: 'Ошибка',
        //     message: 'Произошла ошибка при загрузке списка ПУ ТРИЦ. Попробуйте позже',
        //     type: 'error',
        // }));
    }
    dispatch(setTricMetersLoading(false));
};

export const sendTricMetersEntries = (
    meterEntries: Partial<CreateMeterEntriesRequest>,
    accountId: string,
    tricAccountId: string
): AppThunk => async (dispatch) => {
    dispatch(setTricMeterEntriesSending(true));

    try {
        const httpWrapperOptions = new HttpWrapperOptions();
        httpWrapperOptions.queryParams = { accountId: tricAccountId };
        httpWrapperOptions.Timeout(MS_IN_SECOND * SECONDS_IN_MINUTE);

        const respone = await httpService.post<CreateMeterEntriesResponse>(
            appUrls.api_send_client_tric_meters,
            meterEntries,
            httpWrapperOptions
        );

        const messageBody: ReactNode[] = [];

        if (respone.length) {
            let warningFlag = false;
            respone.forEach(res => {

                const [code] = Object.keys(res) as TricServiceCode[];
                if (res[code]?.apply) {
                    meterEntries[code]?.forEach(passedMeterEntry => {
                        dispatch(updateMeterReadingsByMeterId({ ...passedMeterEntry, accountId }));
                    });
                }
                else {
                    warningFlag = true;
                }
                const ServiceTypeIcon = getTricServiceIcon(code);

                messageBody.push(
                    <div style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ marginRight: 5 }}>
                                <ServiceTypeIcon />
                            </div>
                            <b>{(TricServiceNameByCode[code])}:</b>
                        </div>
                        {res?.[code]?.status} <br />
                    </div>);
            });

            dispatch(modalNotificationsPush({
                title: 'Ваши показания переданы!',
                message: messageBody,
                type: warningFlag ? 'warning' : 'success',
                cancelButtonTitle: 'Закрыть',
                onCancel: () => {
                    dispatch(setTricMeterEntriesSending(false));
                }
            }));
        }
        else {
            dispatch(modalNotificationsPush({
                title: 'Показания не приняты',
                message: 'Попробуйте позже',
                type: 'error',
            }));
        }


    } catch (err: any) {
        const modalNotification = buildModalNotificationFromMeterEntriesValidationError(err);
        dispatch(modalNotificationsPush(modalNotification));
    } finally {
        dispatch(setTricMeterEntriesSending(false));
    }
};
